import { Accordion } from "@mui/material";
import { PageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { useEffect, useRef } from "react";
import { IGroupedV2Clause } from "./types";
import { V2ClauseAccordionDetails } from "./V2ClauseAccordionDetails";
import { V2ClauseAccordionSummary } from "./V2ClauseAccordionSummary";

interface V2ClauseAccordionProps {
    clauseGroup: IGroupedV2Clause;
    expanded: boolean;
    onToggle: () => void;
    pageNavigationPluginInstance: PageNavigationPlugin;
    isHighlighted?: boolean;
    highlightedScopeClauseId?: number;
    onClauseSelect?: (scopeClauseId: number | null) => void;
}

/**
 * Individual clause accordion for V2 Topics UI
 * Wraps summary and details components with expand/collapse behavior
 */
export const V2ClauseAccordion = ({
    clauseGroup,
    expanded,
    onToggle,
    pageNavigationPluginInstance,
    isHighlighted = false,
    highlightedScopeClauseId,
    onClauseSelect,
}: V2ClauseAccordionProps) => {
    const accordionRef = useRef<HTMLDivElement>(null);
    const panelId = `clause-panel-${clauseGroup.clause.id}`;

    // Scroll into view when highlighted
    useEffect(() => {
        if (isHighlighted && accordionRef.current) {
            setTimeout(() => {
                accordionRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
            }, 100);
        }
    }, [isHighlighted]);

    return (
        <Accordion
            ref={accordionRef}
            disableGutters
            elevation={0}
            expanded={expanded}
            onChange={onToggle}
            sx={{
                "&:before": { display: "none" },
                transition: "all 0.3s ease",
            }}
        >
            <V2ClauseAccordionSummary
                clauseGroup={clauseGroup}
                panelId={panelId}
                isExpanded={expanded}
            />
            <V2ClauseAccordionDetails
                clauseGroup={clauseGroup}
                panelId={panelId}
                expanded={expanded}
                pageNavigationPluginInstance={pageNavigationPluginInstance}
                highlightedScopeClauseId={highlightedScopeClauseId}
                onClauseSelect={onClauseSelect}
            />
        </Accordion>
    );
};
